import { useMemo } from "react";
import styled, { css } from "styled-components";

import { getIcon, icons } from "./IconButton";

type colors = "green" | "blue" | "yellow" | "white" | "dark";
const fontColors: { [key in colors]: string } = {
  green: "rgba(255, 255, 255, 1)",
  blue: "rgba(255, 255, 255, 1)",
  yellow: "rgba(33, 37, 41, 1)",
  white: "rgba(33, 37, 41, 1)",
  dark: "rgba(255, 255, 255, 1)",
};

const borderColors: { [key in colors]: string } = {
  green: "rgba(48, 158, 91, 1)",
  blue: "rgba(0, 117, 188, 1)",
  yellow: "rgba(249, 191, 43, 1)",
  white: "rgba(229, 229, 229, 1)",
  dark: "rgba(33, 37, 41, 1)",
};

const backgroundColors: { [key in colors]: string } = {
  green: "rgba(48, 158, 91, 1)",
  blue: "rgba(0, 117, 188, 1)",
  yellow: "rgba(249, 191, 43, 1)",
  white: "rgba(255, 255, 255, 1)",
  dark: "rgba(33, 37, 41, 1)",
};

const disabledStyle = css`
  color: #212529;
  /* Mediumgrijs */
  background: #e5e5e5;

  /* Mediumgrijs */
  border: 1px solid #e5e5e5;
  border-radius: 5px;
`;

/**
 * Primary UI component for user interaction
 */
const StyledButton = styled.button<Props>`
  min-width: ${(props) =>
    props.size === "medium"
      ? "150px"
      : props.size === "small"
      ? "50px"
      : "100%"};
  height: 50px;

  display: flex;
  gap: 8px;
  justify-content: space-between;
  align-items: center;
  cursor: ${(props) => (props.disabled ? "default" : "pointer")};
  border-radius: 5px;
  text-align: center;
  padding: 0 13px;

  color: ${(props) =>
    props.transparentBackground
      ? backgroundColors[props.color || "white"]
      : props.bordered
      ? props.color !== "white"
        ? backgroundColors[props.color || "dark"]
        : "rgba(33, 37, 41, 1)"
      : fontColors[props.color || "white"]};
  border: 2px solid ${(props) =>
    props.transparentBackground
      ? backgroundColors[props.color || "white"]
      : props.bordered
      ? borderColors[props.color || "white"]
      : backgroundColors[props.color || "white"]};
  background-color: ${(props) =>
    props.transparentBackground
      ? "transparent"
      : props.bordered
      ? "rgba(0, 0, 0, 0)"
      : backgroundColors[props.color || "white"]};
  ${(props) => (props.disabled ? disabledStyle : "")}}
`;

const Label = styled.span`
  width: 100%;
  font-family: "Roboto";
  font-style: normal;
  font-weight: 700;
  font-size: 13px;
  line-height: 120%;
  margin: 0;
  text-align: center;
`;
const Icon = styled.div`
  display: flex;
  justify-items: center;
`;

export const Button = (props: Props) => {
  const renderIcon = useMemo(() => {
    return props.icon ? (
      <Icon>
        {getIcon(
          props.icon,
          props.iconSize,
          false,
          fontColors[props.color || "white"]
        )}
      </Icon>
    ) : null;
  }, [props.color, props.icon, props.iconSize]);

  return (
    <StyledButton {...props} type={props.type}>
      {props.iconSide === "left" && renderIcon}
      {props.label && <Label>{props.label}</Label>}
      {props.iconSide === "right" && renderIcon}
    </StyledButton>
  );
};

interface Props {
  type?: "button" | "submit";
  /**
   * What color should the component be?
   */
  color?: colors;
  /**
   * Should the button only contain a border?
   */
  bordered?: boolean;
  /**
   * How large should the button be?
   */
  size?: "small" | "medium" | "full";
  /**
   * Optional additional icon to be displayed left of the label
   */
  icon?: icons;
  iconSize?: "small" | "medium" | "large";
  /**
   * Text to be displayed in the center
   */
  label: string;
  transparentBackground?: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
  iconSide: "left" | "right";
}

Button.defaultProps = {
  type: "button",
  iconSide: "left",
  bordered: false,
  color: "white",
  size: "medium",
  iconSize: "small",
  transparentBackground: false,
};

export default Button;
